import { Box, Flex, Grid, Group, Pagination, Text, Button, TextInput, Center, Loader, Tooltip } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { QRCodeService } from '@/services/QRCodeService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { QRCodeCard } from './components/QRCodeCard';
import { useQRCodesStore } from '@/stores/useQRCodesStore';
import { TotalResultsMenu } from '@/components/Menus/TotalResultsMenu.tsx/TotalResultsMenu';
import { useDebouncedValue } from '@mantine/hooks';
import { Helmet } from 'react-helmet-async';

interface QRCode {
    _id: string;
    name: string;
    url: string;
    workspace_id: string;
    created_at: string;
    updated_at: string;
    [key: string]: any; // for any additional properties
}

export function QRCodesPage() {
    const [defaultWorkspace] = useWorkspaceStore(state => [state.defaultWorkspace]);
    const [qrCodes, setQRCodes] = useState<QRCode[]>([]);
    const [loading, setLoading] = useState(true);

    // Use the QRCodesStore for pagination and search
    const [search, setSearch, page, setPage, lastPage, setLastPage, totalResults, setTotalResults] =
        useQRCodesStore(state => [
            state.search,
            state.setSearch,
            state.page,
            state.setPage,
            state.lastPage,
            state.setLastPage,
            state.totalResults,
            state.setTotalResults
        ]);

    const [debouncedSearch] = useDebouncedValue(search, 300);
    const navigate = useNavigate();

    useEffect(() => {
        fetchQRCodes();
    }, [debouncedSearch, page, totalResults]);

    const fetchQRCodes = async () => {
        setLoading(true);
        await new QRCodeService()
            .getAll(defaultWorkspace?._id || '', page, totalResults, debouncedSearch)
            .then((res) => {
                if (res.data.status) {
                    setQRCodes(res.data.data.data);
                    setLastPage(res.data.data.last_page);
                }
            })
            .catch((err) => console.log(err));
        setLoading(false);
    };

    const confirmDelete = (id: any) => {
        modals.openConfirmModal({
            title: <Text size="lg" fw={500}>Delete QR Code</Text>,
            children: <Text size="sm">Are you sure? This process cannot be undone.</Text>,
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: () => handleDelete(id),
        });
    };

    const handleDelete = async (id: any) => {
        await new QRCodeService()
            .delete(defaultWorkspace?._id || '', id)
            .then((res) => {
                if (res.data.status) {
                    fetchQRCodes(); // Refresh the list after deletion
                    notifications.show({
                        color: 'green',
                        message: 'QR code deleted successfully',
                    });
                }
            })
            .catch((err) => console.log(err));
    };

    return (
        <Box>
            <Helmet>
                <title>QR Codes | Replug</title>
                <meta name="description" content="Manage your QR codes with Replug. Create, edit, and track QR codes for various content types including WiFi, PDF, and text." />
                <meta property="og:title" content="QR Codes | Replug" />
                <meta property="og:description" content="Manage your QR codes with Replug. Create, edit, and track QR codes for various content types including WiFi, PDF, and text." />
                <meta property="og:type" content="website" />
                <meta name="twitter:title" content="QR Codes | Replug" />
                <meta name="twitter:description" content="Manage your QR codes with Replug. Create, edit, and track QR codes for various content types including WiFi, PDF, and text." />
            </Helmet>

            <Flex align="center" pl="lg" pt="xs">
                <Text size="xl" fw="bold" mr="sm">QR Codes</Text>
                <Tooltip
                    position="bottom"
                    multiline
                    w={450}
                    fz="xs"
                    label="Create customized QR codes that link to your content. Track scans and engagement metrics. Use them on printed materials, presentations, or product packaging to drive traffic to your digital content."
                >
                    <FontAwesomeIcon
                        style={{
                            color: 'var(--mantine-color-gray-6)',
                        }}
                        icon={faCircleInfo}
                    />
                </Tooltip>
            </Flex>

            <Grid my="md" px="lg" grow>
                <Grid.Col span={5}>
                    <Flex align="center">
                        <Button
                            size="xs"
                            onClick={() => navigate(`/qr-codes/${defaultWorkspace?._id}/setup`)}
                        >
                            Create new
                        </Button>
                        <TextInput
                            w={160}
                            size="xs"
                            ml={8}
                            value={search}
                            onChange={(e) => setSearch(e.currentTarget.value)}
                            placeholder="Search QR Codes"
                            leftSection={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                        />
                    </Flex>
                </Grid.Col>
            </Grid>

            <Flex direction="column" mx="md" style={{ background: 'var(--mantine-color-white)' }}>
                <Box p="md" mx="md">
                    {loading ? (
                        <Center p="xl"><Loader size="sm" /></Center>
                    ) : qrCodes.length === 0 ? (
                        <Flex justify="center" align="center" direction="column" p="lg">
                            <Text mb="md" fz={14} ta="center">
                                {search ? `No QR codes found for "${search}"` : "You haven't created any QR codes yet."}
                            </Text>
                            <Button onClick={() => navigate(`/qr-codes/${defaultWorkspace?._id}/setup`)}>
                                Create QR Code
                            </Button>
                        </Flex>
                    ) : (
                        <Group style={{ justifyContent: 'center', gap: '30px' }}>
                            {qrCodes.map((qr) => (
                                <QRCodeCard
                                    key={qr._id}
                                    qrCode={qr}
                                    onEdit={() => navigate(`/qr-codes/${defaultWorkspace?._id}/setup/${qr._id}`)}
                                    onDelete={() => confirmDelete(qr._id)}
                                />
                            ))}
                        </Group>
                    )}
                </Box>
            </Flex>

            {/* Pagination controls */}
            {qrCodes.length > 0 && (
                <Box mx="sm" mb="xl" pb="lg" mt="sm">
                    <Group justify="space-between">
                        <Pagination value={page} onChange={setPage} size="xs" total={lastPage} />
                        <TotalResultsMenu value={totalResults} onChange={setTotalResults} />
                    </Group>
                </Box>
            )}
        </Box>
    );
}